import { z } from "zod";

/**
 * Common reusable Zod schemas for API operations across the server package.
 * Import these schemas instead of redeclaring them in individual schema files.
 *
 * Usage:
 * import { objectIdString, filterWhereClause, sortConfig, statusResponse } from "../../helpers/schemas/common.schema.js";
 */

// ============================================================================
// MongoDB ObjectId Schema
// ============================================================================

/**
 * MongoDB ObjectId as a 24-character hex string with validation.
 */
export const objectIdString = z
  .string()
  .trim()
  .regex(/^[a-fA-F0-9]{24}$/, "Invalid ObjectId format")
  .meta({
    description: "MongoDB ObjectId as 24-character hex string",
    example: "64b2c123df890abc1234abcd",
  })
  .brand<"ObjectId">();

/**
 * Tag an id ALREADY known to be a valid ObjectId as an {@link ObjectIdString} — values that came out
 * of MongoDB (`doc._id`, `insertedId`) or that the server generated, which have already passed the
 * trust boundary. Accepts an `ObjectId` structurally, so helpers needs no mongodb dependency.
 *
 * ⚠️ For anything CLIENT-supplied use `objectIdString.parse()` / `.safeParse()` instead — that
 * boundary is what the brand exists to protect.
 */
export function toObjectIdString(
  id: { toString(): string } | string,
): ObjectIdString;
export function toObjectIdString(
  id: { toString(): string } | string | null | undefined,
): ObjectIdString | undefined;
export function toObjectIdString(
  id: { toString(): string } | string | null | undefined,
): ObjectIdString | undefined {
  // Mirrors `x?._id?.toString()` semantics so nullish chains keep propagating undefined
  // instead of stringifying into the literal "undefined".
  return id == null ? undefined : (String(id) as ObjectIdString);
}

// ============================================================================
// Email Schema
// ============================================================================

/**
 * Email address, trimmed BEFORE validation. In Zod v4 a `.trim()` chained after `z.email()` runs
 * after validation, so surrounding whitespace fails the check — hence the `.pipe()`. Max 254 chars
 * (RFC 5321); callers add `.optional()` / `.nullable()`.
 */
export const emailString = z.string().trim().pipe(z.email().max(254)).meta({
  description:
    "Email address (trimmed, RFC-validated, max 254 chars per RFC 5321)",
  example: "user@example.com",
});

// ============================================================================
// AWS / Temp Upload Key Schema
// ============================================================================

/**
 * Key returned by `/fileSystemServer/uploadFileInTemp`. Shape is `<uuid-v4>.<ext>`
 * (see `packages/server/fileUpload/fileUpload.service.ts` – `file.__uuid__`).
 * Use this anywhere a request accepts an awsKey freshly produced by the temp
 * upload endpoint, so path-traversal / arbitrary strings are rejected before
 * they reach `transferFileFromTempToMainStorage`.
 */
export const tempUploadKey = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9]+$/i,
    "awsKey must be a uuid.<extension> produced by /fileSystemServer/uploadFileInTemp",
  )
  .meta({
    description:
      "Temp-upload key (uuid.<extension>) returned by /fileSystemServer/uploadFileInTemp",
    example: "f47ac10b-58cc-4372-a567-0e02b2c3d479.mp4",
  });

export const uuidStorageKey = z
  .string()
  .trim()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    "awsKey must be a bare uuid",
  )
  .meta({
    description:
      "Storage object key: a bare uuid, with no path separators or extension",
    example: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  });

// ============================================================================
// Grid/Query Filter Schemas
// ============================================================================

/**
 * Filter where clause for grid queries.
 * Accepts both record format (object) and array format (Syncfusion DataManager).
 */
export const filterWhereClause = z
  // record (object) OR array (Syncfusion DataManager) — same inputs the previous
  // z.custom accepted, but JSON-Schema-representable so it can be advertised to API/MCP clients.
  .union([z.record(z.string(), z.unknown()), z.array(z.unknown())])
  .optional()
  .meta({
    description:
      "Filter conditions for grid queries (supports both record and array formats)",
  });

/**
 * Build a filter `where` schema locked to an explicit allow-list of filterable fields. SECURITY
 * CONTROL: `buildFilterWhereClause` (server/helpers/queryBuilder.ts) drops each condition's `field`
 * straight into a Mongo query key, so the open {@link filterWhereClause} lets a client filter on —
 * and value-probe — ANY field, `portalId` and `users.modulePermissions.*` included.
 *
 * Accepts all three Syncfusion/SlickGrid shapes: flat predicate, grouped predicate (recursed, every
 * leaf `field` checked), and `{ field: "a,b,c", operator: "globalsearch" }` — globalsearch is
 * NARROWED rather than rejected, disallowed fields dropped from the list.
 *
 * ⚠️ `operator` stays a FREE string (the UI emits `between`, `empty`, `null`, … outside
 * {@link filterOperator}); the field allow-list, not the operator, is the control. `value` stays
 * scalars/arrays-of-scalars so a raw Mongo operator object (`{ $ne: null }`) cannot be smuggled in.
 */
/**
 * Allow-patterns for grids whose filterable columns are DYNAMIC per project and cannot be enumerated
 * statically. Pass via the `patterns` option of {@link filterWhereClauseFor}.
 *
 * - `customFieldFilterPattern` — `customFields.<id>` / `projectLevelCustomFields.<id>`, where <id> is
 *   a 24-hex ObjectId or a 36-char UUID v4 (SlickGrid1/customFields.tsx `getFieldKey`).
 * - `formQuestionFilterPattern` — bare client-generated `questionId` UUID v4, consumed by
 *   `buildFilterWhereClause(..., formsPatch=true)`.
 */
export const customFieldFilterPattern =
  /^(customFields|projectLevelCustomFields)\.([a-fA-F0-9]{24}|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/;
export const formQuestionFilterPattern =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Both dynamic-column patterns together. Safe to pass to ANY grid endpoint: these patterns
 * only match user-defined custom-field / form-question columns (`customFields.<id>`,
 * `<uuid>`) — never internal system fields — so they never reopen the arbitrary-field
 * hole, and including them prevents 400s on grids that render dynamic custom-field columns.
 */
export const dynamicCustomFieldPatterns: readonly RegExp[] = [
  customFieldFilterPattern,
  formQuestionFilterPattern,
];

/**
 * A single filter `value` exactly as {@link filterWhereClauseFor}'s internal `filterValue` constrains it
 * AT RUNTIME: scalars and arrays-of-scalars ONLY. That restriction is itself the security control — it is
 * what stops a client smuggling a raw Mongo operator object (`{ $ne: null }`, `{ $gt: "" }`) into a query
 * key, since the query builder drops values in verbatim. Do NOT widen the schema.
 */
type FilterConditionScalar = string | number | boolean | null;
export type FilterConditionValue =
  FilterConditionScalar | FilterConditionScalar[];

/**
 * One leaf/group condition of a `where` payload validated by {@link filterWhereClauseFor}, and the
 * element type its `z.infer` yields. Every member is optional because the schema accepts all three
 * Syncfusion/SlickGrid shapes, so nothing may be assumed present; the field ALLOW-LIST enforced at
 * runtime by `superRefine` — not this type — is the security control.
 *
 * ⚠️ `value` is deliberately `unknown`, a sound widening of {@link FilterConditionValue}: the web
 * grid's `PredicateShape` (web/src/services/queryBuilder.ts) declares `value: unknown`, and a
 * narrower declaration here stops `packages/web` compiling. Runtime rejection of `{ $ne: null }` is
 * unaffected. For the same reason do NOT add a `[key: string]: unknown` index signature — TypeScript
 * grants interfaces no implicit index signature, so it would reject every interface-typed caller.
 */
export interface FilterWhereCondition {
  // Each member is `| undefined` EXPLICITLY, not just `?`. Under `exactOptionalPropertyTypes` those
  // differ: `field?: string` means the key may be ABSENT but never explicitly `undefined`, whereas
  // Zod's `.optional()` produces exactly `string | undefined`. Without the annotation the inferred
  // schema output stops being assignable to this type.
  field?: string | undefined;
  operator?: string | undefined;
  value?: unknown;
  isComplex?: boolean | undefined;
  condition?: string | undefined;
  predicates?: FilterWhereCondition[] | undefined;
  /**
   * Syncfusion/SlickGrid case+accent flags. `looseObject` passes them through at runtime, and both are
   * already declared by {@link whereClauseSchema}'s predicate and by `queryBuilder.ts`'s FilterCondition.
   * They MUST be named here rather than left to an index signature: several `packages/web` actions wrap
   * their payload in type-fest's `Exact<>`, which turns any key absent from this interface into `never`.
   */
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
}

/**
 * @param allowedFields exact field names a client may filter on.
 * @param opts.patterns optional RegExps; a field is also allowed if it matches any pattern
 *   (use for grids with dynamic custom-field / form-question columns — see the pattern consts).
 */
export function filterWhereClauseFor(
  allowedFields: readonly string[],
  opts: { patterns?: readonly RegExp[] } = {},
) {
  const allowed = new Set(allowedFields);
  const patterns = opts.patterns ?? [];
  const isAllowed = (field: string) =>
    allowed.has(field) || patterns.some((re) => re.test(field));
  const allowedList =
    allowedFields.join(", ") +
    (patterns.length ? " (plus dynamic custom-field columns)" : "");

  // Scalars and arrays-of-scalars only — never a raw object (blocks `{ $ne: ... }` etc.).
  const filterScalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
  const filterValue = z.union([filterScalar, z.array(filterScalar)]).optional();

  // Narrow a global-search field list to allow-listed columns only. Runs as a
  // `z.preprocess` (input coercion) rather than a trailing `.transform()` ON PURPOSE:
  // `.transform()` makes `z.toJSONSchema` throw ("Transforms cannot be represented"),
  // which would force every endpoint using this filter to fall back to an opaque
  // `payload` shape when advertised to API/MCP clients. `z.preprocess(fn, inner)` does
  // the same narrowing but stays JSON-Schema-representable. Do NOT switch this back to
  // `.transform()`.
  const narrowGlobalSearch = (raw: unknown) => {
    const cond = raw as { operator?: string; field?: unknown } | null;
    if (
      cond &&
      cond.operator === "globalsearch" &&
      typeof cond.field === "string"
    ) {
      const narrowed = cond.field
        .split(",")
        .map((f) => f.trim())
        .filter((f) => isAllowed(f));
      return { ...cond, field: narrowed.join(",") };
    }
    return raw;
  };

  // Recursive schemas need an explicit annotation (TypeScript cannot infer a self-referential type), and
  // that annotation is exactly what `z.infer` reports. Naming the real node type here — instead of the
  // bare `z.ZodType`, i.e. `ZodType<unknown, unknown>`, this used to carry — is what lets
  // `FilterWhereCondition` flow out to every routed grid endpoint. The INPUT side is left at `ZodType`'s
  // `unknown` default on purpose: `z.preprocess` genuinely accepts unknown input, so nothing that used to
  // parse stops parsing and no caller's request-payload type is narrowed.
  const condition: z.ZodType<FilterWhereCondition> = z.lazy(() =>
    z.preprocess(
      narrowGlobalSearch,
      z
        .looseObject({
          field: z.string().optional(),
          operator: z.string().optional(),
          value: filterValue,
          isComplex: z.boolean().optional(),
          condition: z.string().optional(),
          predicates: z.array(condition).optional(),
        })
        .superRefine((cond, ctx) => {
          // Grouped conditions carry no `field`; their leaves are validated recursively
          // via `predicates`. Global search is narrowed (not rejected) by the preprocess
          // above, so it does not need an allow-list check here.
          if (cond.operator === "globalsearch") return;
          if (typeof cond.field !== "string" || cond.field.length === 0) return;
          if (!isAllowed(cond.field)) {
            ctx.addIssue({
              code: "custom",
              path: ["field"],
              message: `Filtering on "${cond.field}" is not allowed. Filterable fields: ${allowedList}.`,
            });
          }
        }),
    ),
  );

  return z
    .array(condition)
    .optional()
    .meta({
      description:
        "Filter for list/grid queries — an ARRAY of conditions ANDed together, e.g. " +
        `[{ field: "${allowedFields[0] ?? "name"}", operator: "contains", value: "Tower-A" }]. ` +
        `Only these fields may be filtered: ${allowedList}.`,
    });
}

/**
 * Sort configuration for grid queries.
 * Specifies the field to sort by and direction.
 */
export const sortConfig = z
  .object({
    // Non-empty: an empty sort field resolves to `{ "": -1 }`, which Mongo rejects with
    // "FieldPath cannot be constructed with empty string" — reject it at the boundary instead.
    name: z.string().min(1).meta({
      description: "Field name to sort by",
    }),
    direction: z.enum(["ascending", "descending"]).optional().meta({
      description: "Sort direction, defaults to ascending if not specified",
    }),
  })
  .meta({
    description: "Sort configuration for grid queries",
  });

/**
 * Array of sort configurations for multi-column sorting.
 */
export const sortConfigArray = z.array(sortConfig).optional().meta({
  description: "Array of sort configurations for multi-column sorting",
});

/**
 * Build a `sorted` schema locked to an allow-list of sortable columns; companion to
 * {@link filterWhereClauseFor}. `buildFilterSortClause` drops `sortClause[0].name` straight into a
 * Mongo sort key, so the open {@link sortConfig} lets a client order-probe ANY field and force an
 * unindexed blocking `$sort` (DoS). Disallowed columns are REJECTED at the Zod boundary (400).
 *
 * Drop-in for `sortConfigArray`. `opts.patterns` (pass {@link dynamicCustomFieldPatterns}) also
 * allows dynamic custom-field / form-question columns. The query builder applies only
 * `sortClause[0]`, but every entry is still checked, so a bad column at index 1+ still 400s.
 */
export function sortConfigFor(
  allowedFields: readonly string[],
  opts: { patterns?: readonly RegExp[] } = {},
) {
  const allowed = new Set(allowedFields);
  const patterns = opts.patterns ?? [];
  const isAllowed = (name: string) =>
    allowed.has(name) || patterns.some((re) => re.test(name));
  const allowedList =
    allowedFields.join(", ") +
    (patterns.length ? " (plus dynamic custom-field columns)" : "");

  const entry = z
    .object({
      // Non-empty (an empty sort field resolves to `{ "": -1 }`, which Mongo rejects) AND allow-listed.
      name: z
        .string()
        .min(1)
        .meta({
          description: `Column to sort by. Sortable columns: ${allowedList}.`,
        }),
      direction: z.enum(["ascending", "descending"]).optional().meta({
        description: "Sort direction, defaults to ascending if not specified",
      }),
    })
    // superRefine (NOT .transform) keeps the schema z.toJSONSchema-representable, like filterWhereClauseFor.
    .superRefine((cfg, ctx) => {
      if (!isAllowed(cfg.name)) {
        ctx.addIssue({
          code: "custom",
          path: ["name"],
          message: `Sorting on "${cfg.name}" is not allowed. Sortable columns: ${allowedList}.`,
        });
      }
    });

  return z
    .array(entry)
    .optional()
    .meta({
      description:
        "Sort configuration for list/grid queries — an array of { name, direction? }; only the first entry is " +
        `applied server-side. Only these columns may be sorted: ${allowedList}.`,
    });
}

// ============================================================================
// Common Response Schemas
// ============================================================================

/**
 * Success branch: status is true, no errorMessage.
 */
export const statusResponseSuccess = z.object({
  status: z.literal(true),
});

/**
 * Failure branch: status is false. `errorMessage` is OPTIONAL — see the note on the member itself
 * for what that costs and why it is not yet `z.string()`.
 */
export const statusResponseFailure = z.object({
  status: z.literal(false),
  /**
   * ⚠️ NOT guaranteed on the wire. There is no longer a response wrapper filling this in: the
   * `ensureFailureMessage` middleware that used to stamp `"Something went wrong"` onto every
   * message-less `{ status: false }` body was DELETED on 2026-09-09, after each refusal was given a
   * reason at its own `return`. So the `?? "Something went wrong"` fallbacks in the frontends are
   * LOAD-BEARING, not belt-and-braces — do not "clean them up" as dead code.
   *
   * Write a real reason at the service. A refusal with no `errorMessage` renders an empty toast.
   *
   * Deleting the wrapper also FIXED two things it was breaking, which is why it went rather than
   * being kept as a safety net:
   *   - it stamped a failure message onto bodies that are not failures at all (`/getAssetTypes`
   *     answers a successful list as `status: false`, and its schema says the PRESENCE of
   *     `errorMessage` — not `status` — is what marks a real refusal; the wrapper set it on every
   *     response and destroyed that discriminator),
   *   - and it never covered `res.send` refusals or bodies that already carried a non-`undefined`
   *     but empty reason (`errorMessage: ""` / `[]`), so the paths most likely to need it were the
   *     ones it silently skipped.
   *
   * ⚠️ WHY NOT `z.string()` (required) YET. Measured 2026-09-09: requiring it takes the server from
   * 1 error to 27. That number understates the work — 21 of the 27 are the `registerRoutes` tuple
   * mismatch, which reports only a controller's FIRST offending route, so each fix reveals the next.
   * It is a campaign in its own right, not a one-line change. (It was 174 before the output-bag
   * migration, so the direction is right.)
   */
  errorMessage: z.string().optional(),
  /**
   * Machine-readable counterpart to `errorMessage` (helpers/errorCodes.ts), so a client can branch on
   * the failure instead of matching on prose. Use the specific code where one fits;
   * `SOMETHING_WENT_WRONG` (9000) is the catch-all. Send it wherever you send an `errorMessage` —
   * nothing fills it in for you any more. Optional for the same reason as `errorMessage` above.
   */
  errorCode: z.number().optional(),
});

/**
 * Status-only response, for routes that return no payload.
 * When status is false, `errorMessage` MAY carry a reason — callers must keep a fallback.
 * Use {@link standardResponse} when there IS a payload.
 */
export const statusResponse = z.discriminatedUnion("status", [
  statusResponseSuccess,
  statusResponseFailure,
]);

/**
 * THE standard response shape; every route uses this or {@link statusResponse}.
 *   success -> { status: true, ...payload }   failure -> { status: false, errorMessage?, errorCode? }
 *
 * The payload sits on the SUCCESS ARM ONLY, so callers must narrow on `status` first.
 *
 * ⚠️ A route needing structured data on FAILURE (`lockedUntil`, `attemptsRemaining`) builds its own
 * union locally from `statusResponseSuccess` / `statusResponseFailure` — never widen this helper.
 */
export function standardResponse<T extends z.ZodRawShape>(shape: T) {
  return z.discriminatedUnion("status", [
    statusResponseSuccess.extend(shape),
    statusResponseFailure,
  ]);
}

/**
 * Standard API response schema with error fields.
 * Use this for responses that may include error information.
 */
export const apiResponse = z
  .object({
    status: z.boolean().meta({
      description: "Indicates whether the operation was successful",
    }),
    error: z.string().optional().meta({
      description: "Short error description",
    }),
    errorCode: z.number().optional().meta({
      description: "Numeric error code for programmatic handling",
    }),
    errorMessage: z.string().optional().meta({
      description: "Detailed error message",
    }),
    message: z.string().optional().meta({
      description: "Success or informational message",
    }),
  })
  .meta({
    description: "Standard API response with optional error fields",
  });

/**
 * Standard API response with data field.
 * Use this for responses that return data on success.
 */
export const apiResponseWithData = apiResponse
  .extend({
    data: z.unknown().optional().meta({
      description: "Response data payload",
    }),
  })
  .meta({
    description: "Standard API response with data field",
  });

/**
 * Minimal service response shape shared by many backend service methods.
 * Mirrors the former `BaseResponse` interface (helpers/scheduleInterface.ts).
 */
export const baseResponse = z
  .object({
    status: z.boolean(),
    message: z.string().optional(),
    errorMessage: z.string().optional(),
    errorCode: z.union([z.number(), z.string()]).optional(),
  })
  .meta({
    description: "Base service response with optional message/error fields",
  });
export type BaseResponse = z.infer<typeof baseResponse>;

// ============================================================================
// Grid Request Value Schema
// ============================================================================

/**
 * Common grid request value schema.
 * Used for paginated, sortable, filterable grid requests.
 */
export const gridRequestValue = z
  .object({
    where: filterWhereClause,
    sorted: sortConfigArray,
    skip: z.number().nonnegative().optional().meta({
      description: "Number of records to skip for pagination",
    }),
    take: z.number().nonnegative().optional().meta({
      description: "Number of records to take for pagination",
    }),
    requiresCounts: z.boolean().optional().meta({
      description: "Whether to return total count for pagination",
    }),
  })
  .meta({
    description:
      "Common grid request parameters for pagination, sorting, and filtering",
  });

// ============================================================================
// Where Clause / Predicate Schema
// ============================================================================

/**
 * Single filter predicate for grid queries.
 */
const predicateSchema = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.unknown(),
  isComplex: z.boolean().optional(),
  ignoreCase: z.boolean().optional(),
  ignoreAccent: z.boolean().optional(),
});

/**
 * Where clause schema supporting both simple predicates and compound conditions.
 * Used across multiple domains (estimates, currency, etc.) for grid filtering.
 */
export const whereClauseSchema = z
  .array(
    z.union([
      predicateSchema,
      z.object({
        condition: z.string(),
        isComplex: z.boolean().optional(),
        ignoreCase: z.boolean().optional(),
        ignoreAccent: z.boolean().optional(),
        predicates: z.array(predicateSchema),
      }),
    ]),
  )
  .optional();

// ============================================================================
// Type Exports
// ============================================================================

export type ObjectIdString = z.infer<typeof objectIdString>;
export type EmailString = z.infer<typeof emailString>;
export type TempUploadKey = z.infer<typeof tempUploadKey>;
export type FilterWhereClause = z.infer<typeof filterWhereClause>;
export type SortConfig = z.infer<typeof sortConfig>;
export type SortConfigArray = z.infer<typeof sortConfigArray>;
export type StatusResponse = z.infer<typeof statusResponse>;
export type ApiResponse = z.infer<typeof apiResponse>;
export type ApiResponseWithData = z.infer<typeof apiResponseWithData>;
export type GridRequestValue = z.infer<typeof gridRequestValue>;
export type WhereClause = z.infer<typeof whereClauseSchema>;
